import { usePricingRights } from '@util/PricingRightsHook';
import { pricingDayAction } from '@util/PricingRights';
import React from 'react';
import { PaperAirplaneIcon, EyeIcon } from '@heroicons/react/16/solid';
import Utilities from '@util/Utilities';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import _isEmpty from 'lodash/isEmpty';
import _groupBy from 'lodash/groupBy';
import Message from '@app/component/message/Message';
import Modal from '@app/component/overlays/modal/Modal';
import useModal from '@app/component/overlays/modal/ModalRef';
import FormButton from '@app/component/form/button/FormButton';
import { AbstractButton } from '@app/site/_form/button/AbstractButton';
import useAppForm from '@app/site/_form';
import ManualPricingIntegrationInput from '@app/model/pricing-automation/input/ManualPricingIntegrationInput';
import PricingRoomWithPriceOutput from '@app/model/pricing-automation/output/PricingRoomWithPriceOutput';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import AvailabilityDailyStateProvider from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';
import PriceRecommendationStateSnapshot from '@app/model/market_state/output/PriceRecommendationStateSnapshot';
import { DrawerProps } from '@app/component/overlays/drawer/Drawer';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';
import MarketStateSnapshotCalendarDayPriceRecommendationDrawer
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/overlay/MarketStateSnapshotCalendarDayPriceRecommendationDrawer';
import BulkAdjustmentBar from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/bulk-adjustment/BulkAdjustmentBar';
import {
	applyBulkAdjustment,
	BulkAdjustmentType,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/bulk-adjustment/BulkAdjustment.utilities';
import PriceEditRoomsTable, {
	PriceEditGroup,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/price-edit/PriceEditRoomsTable';

const MarketStateSnapshotCalendarDayTableRowPriceSubmissionAction: React.FunctionComponent<{
	priceRecommendationState: PriceRecommendationStateSnapshot | undefined,
	statDate: string
}> = ({ priceRecommendationState, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { canViewRecommendations, canChangePrices } = usePricingRights(companyHotel);
	const action = pricingDayAction(canViewRecommendations, canChangePrices, priceRecommendationState !== undefined);
	if (action === null || action === undefined) return null;


	if (priceRecommendationState !== undefined) {
		return <RenderRecommendationPricesButton readOnly={action === 'view'} priceRecommendationState={priceRecommendationState} statDate={statDate} />;
	}

	return <RenderNonRecommendationPricesButton statDate={statDate} />;
};

type RenderRecommendationPricesButtonProps = Omit<DrawerProps, 'title' | 'ref'> & {
	readOnly: boolean,
	priceRecommendationState: PriceRecommendationStateSnapshot,
	statDate: string,
}

// FormButton forwards no title, so the hover text sits on a wrapper span; the sr-only span names the button.
const RenderButton: React.FunctionComponent<{ onClick: () => any, readOnly?: boolean }> = ({ onClick, readOnly = false }) => <span
	title={readOnly ? 'View recommendation' : 'Change price'}>
	<FormButton
		type="button"
		state="text"
		onClick={onClick}
		className="inline-flex h-7 w-7 items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
	>
		<span className="sr-only">{readOnly ? 'View recommendation' : 'Change price'}</span>
		{readOnly ? <EyeIcon className="h-4 w-4" aria-hidden="true" /> : <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />}
	</FormButton>
</span>;

const RenderRecommendationPricesButton = React.memo(
	function RenderDetailsLink({ priceRecommendationState, statDate, readOnly, ...props }: RenderRecommendationPricesButtonProps) {
		const drawer = useDrawer();

		return <>
			<RenderButton readOnly={readOnly} onClick={() => drawer.open()} />
			<MarketStateSnapshotCalendarDayPriceRecommendationDrawer statDate={statDate} {...props} ref={drawer.ref} />
		</>;
	});

const RenderNonRecommendationPricesButton: React.FunctionComponent<{ statDate: string }> = ({ statDate }) => {
	const modal = useModal();
	const formId = React.useId();
	const [canSubmit, setCanSubmit] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	// The Reset of the footer reaches the form inside the modal through this counter.
	const [resetEpoch, setResetEpoch] = React.useState(0);
	return <>
		<RenderButton onClick={() => modal.open()} />

		<Modal title={`Submit Prices - ${Utilities.formatDateString(statDate, 'DD MMMM YYYY', false)}`}
		ref={modal.ref}
		width="2xlarge"
		onOpen={() => {
						setCanSubmit(false);
						setIsSubmitting(false);
						setResetEpoch(0);
					}}
		actions={<>
						<AbstractButton state="text" type="button" disabled={!canSubmit || isSubmitting}
						onClick={() => setResetEpoch(epoch => epoch + 1)}>
							Reset
						</AbstractButton>
						<FormButton state="primary" type="submit" form={formId} disabled={!canSubmit || isSubmitting}
			isLoading={isSubmitting}>
							Submit
						</FormButton>
					</>}>
			<ModalContents statDate={statDate}
			formId={formId}
			resetEpoch={resetEpoch}
			onClose={() => modal.close()}
			onCanSubmitChange={setCanSubmit}
			onSubmittingChange={setIsSubmitting} />
		</Modal>
	</>;
};

// A room the table can show: WebHotelier has a price for its PMS rate plan on the date.
type PricedRoomWithPrice = PricingRoomWithPriceOutput & { price: number };

// An Unpriced Master Room leaves out its whole Pricing Architecture and alone names it;
// an Unpriced Derivative Room leaves out only itself.
const splitUnpricedRooms = (roomsWithPrice: PricingRoomWithPriceOutput[]) => {
	const hiddenArchitectureIds = new Set(roomsWithPrice
		.filter(roomWithPrice => roomWithPrice.isAnchor && roomWithPrice.price === null)
		.map(roomWithPrice => roomWithPrice.architectureId));
	const shownRooms: PricedRoomWithPrice[] = [];
	const unpricedRooms: PricingRoomWithPriceOutput[] = [];
	roomsWithPrice.forEach(roomWithPrice => {
		if (hiddenArchitectureIds.has(roomWithPrice.architectureId)) {
			if (roomWithPrice.isAnchor) unpricedRooms.push(roomWithPrice);
		} else if (roomWithPrice.price === null) {
			unpricedRooms.push(roomWithPrice);
		} else {
			shownRooms.push({ ...roomWithPrice, price: roomWithPrice.price });
		}
	});
	return { shownRooms, unpricedRooms };
};

const UnpricedRoomsList: React.FunctionComponent<{ unpricedRooms: PricingRoomWithPriceOutput[] }> = ({ unpricedRooms }) =>
	<div>
		<ul className="mt-2 list-disc space-y-1 pl-5">
			{unpricedRooms.map(roomWithPrice => <li key={roomWithPrice.room.id}>
				{roomWithPrice.room.roomName}
				{!!roomWithPrice.room.rateName && ` (${roomWithPrice.room.rateName})`}
				{roomWithPrice.isAnchor && ': its group is not shown, because this is the main room of the group.'}
			</li>)}
		</ul>
		<p className="mt-2">Add a price for these rates in WebHotelier, or select a different rate in Pricing Architecture.</p>
	</div>;

const ModalContents: React.FunctionComponent<{
	statDate: string,
	formId: string,
	resetEpoch: number,
	onClose: () => any,
	onCanSubmitChange: (canSubmit: boolean) => any,
	onSubmittingChange: (isSubmitting: boolean) => any
}> = ({ statDate, formId, resetEpoch, onClose, onCanSubmitChange, onSubmittingChange }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { t } = useTranslation();

	const { data, isLoading, isSuccess, isError, error } = useQuery({
		queryKey: PricingAutomationQueryKeys.pricingRoomsWithPrice(companyHotel.id, statDate),
		queryFn: () => PricingAutomationService.findCompanyHotelPricingRoomWithPrice(companyHotel.id, statDate),
	});

	const { shownRooms, unpricedRooms } = React.useMemo(() => splitUnpricedRooms(data ?? []), [data]);

	React.useEffect(() => {
		onCanSubmitChange(isSuccess && !_isEmpty(shownRooms));
	}, [shownRooms, isSuccess, onCanSubmitChange]);

	if (isLoading) return <TablePlaceholder />;

	// The executor rejects with a string (the error code) or with an Error: show the one that has text.
	if (isError) return <Message state="error">{typeof error === 'string' ? t(error) : (error as Error | null)?.message ?? 'Unknown error'}</Message>;

	if (isSuccess && _isEmpty(shownRooms)) {
		if (_isEmpty(unpricedRooms)) return <Message state="error">No prices found</Message>;
		return <Message state="error" title={<strong>No prices found</strong>}>
			<UnpricedRoomsList unpricedRooms={unpricedRooms} />
		</Message>;
	}

	return <AvailabilityDailyStateProvider companyHotel={companyHotel} statDate={statDate}>
		{!_isEmpty(unpricedRooms) && <Message state="warning"
		title={<strong>Some rooms have no price in WebHotelier for {Utilities.formatDateString(statDate, 'DD MMMM YYYY', false)}. You cannot submit them here.</strong>}>
			<UnpricedRoomsList unpricedRooms={unpricedRooms} />
		</Message>}
		<PricingSubmissionForm statDate={statDate}
		formId={formId}
		resetEpoch={resetEpoch}
		roomsWithPrice={shownRooms}
		onClose={onClose}
		onSubmittingChange={onSubmittingChange} />
	</AvailabilityDailyStateProvider>;
};

const PricingSubmissionForm: React.FunctionComponent<{
	statDate: string,
	formId: string,
	resetEpoch: number,
	roomsWithPrice: PricedRoomWithPrice[],
	onClose: () => any,
	onSubmittingChange: (isSubmitting: boolean) => any
}> = ({ statDate, formId, resetEpoch, roomsWithPrice, onClose, onSubmittingChange }) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const toaster = useToaster();
	const { t } = useTranslation();
	const tools = useMarketStateTools();
	const queryClient = useQueryClient();
	const [derivativeSynchronizationSuppressionEpoch, setDerivativeSynchronizationSuppressionEpoch] = React.useState(0);

	const groupedRooms = React.useMemo(() => Object.values(_groupBy(roomsWithPrice, room => room.architectureId)), [roomsWithPrice]);

	const defaultValues: ManualPricingIntegrationInput = {
		statDate,
		rates: groupedRooms.map(rooms => ({
			architectureId: rooms[0].architectureId,
			roomPrices: rooms.map(roomWithPrice => ({
				roomId: roomWithPrice.room.id,
				currentPrice: Utilities.roundDouble(roomWithPrice.price, 0),
				submittedPrice: Utilities.roundDouble(roomWithPrice.price, 0),
				isAnchor: roomWithPrice.isAnchor,
			})),
		})),
	};

	const submitPricingMutation = useMutation({
		mutationFn: PricingAutomationService.submitManualPricing,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: PricingAutomationQueryKeys.pricingSubmissions(companyHotel.id),
			}).then(() => {
				toaster.success('Pricing', 'Pricing submitted successfully!');
				tools.reloadMarketStateForDay(statDate);
				onClose();
			});
		},
		onError: (e: string) => toaster.error('Pricing', t(e)),
	});

	React.useEffect(() => {
		onSubmittingChange(submitPricingMutation.isPending);
	}, [onSubmittingChange, submitPricingMutation.isPending]);

	const form = useAppForm({
		defaultValues,
		onSubmit: async ({ value }) => {
			const input: ManualPricingIntegrationInput = {
				...value,
				rates: value.rates.map(rate => ({
					...rate,
					roomPrices: rate.roomPrices.map(roomPrice => ({
						...roomPrice,
						submittedPrice: roomPrice.submittedPrice ?? roomPrice.currentPrice ?? 0,
					})),
				})),
			};

			return submitPricingMutation.mutateAsync({
				companyHotelId: companyHotel.id,
				input,
			}).catch(() => undefined);
		},
	});

	// The Reset of the footer: every New Rate back to the Current Rate, no cascade, the bar cleared.
	const previousResetEpoch = React.useRef(resetEpoch);
	React.useEffect(() => {
		if (previousResetEpoch.current === resetEpoch) return;
		previousResetEpoch.current = resetEpoch;
		setDerivativeSynchronizationSuppressionEpoch(epoch => epoch + 1);
		form.reset();
	}, [form, resetEpoch]);

	// The Bulk Adjustment: every New Rate becomes the Current Rate plus the change, no cascade.
	const applyBulk = (adjustmentType: BulkAdjustmentType, adjustmentValue: number) => {
		setDerivativeSynchronizationSuppressionEpoch(epoch => epoch + 1);
		groupedRooms.forEach((rooms, rateIndex) => {
			rooms.forEach((roomWithPrice, roomIndex) => {
				const adjustedPrice = applyBulkAdjustment(roomWithPrice.price, adjustmentType, adjustmentValue);
				if (adjustedPrice === undefined) return;
				form.setFieldValue(
					`rates[${rateIndex}].roomPrices[${roomIndex}].submittedPrice`,
					adjustedPrice,
				);
			});
		});
	};

	const groups: PriceEditGroup[] = groupedRooms.map((rooms, rateIndex) => ({
		key: `manual-pricing-${rooms[0].architectureId}`,
		rateIndex,
		roomMappingType: rooms[0].roomMappingType,
		rooms: rooms.map((roomWithPrice, roomIndex) => ({
			roomId: roomWithPrice.room.id,
			roomName: roomWithPrice.room.roomName,
			isAnchor: roomWithPrice.isAnchor,
			currentPrice: roomWithPrice.price,
			derivativeRule: roomWithPrice.derivativeRule === undefined || roomWithPrice.derivativeRule === null
				? undefined
				: { type: roomWithPrice.derivativeRule.type, value: roomWithPrice.derivativeRule.value === null ? undefined : Number(roomWithPrice.derivativeRule.value) },
			roomIndex,
		})),
	}));

	return <form.AppForm>
		<form id={formId} onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		}}>
			<p className="mb-2 text-sm text-gray-600">Set the rates for the rooms below and submit to update the rates.</p>
			<BulkAdjustmentBar currency={companyHotel.settings.main.currency}
			onApply={applyBulk}
			resetEpoch={resetEpoch} />
			<PriceEditRoomsTable title="Rates Summary"
			currency={companyHotel.settings.main.currency}
			groups={groups}
			form={form}
			suppressionEpoch={derivativeSynchronizationSuppressionEpoch} />
		</form>
	</form.AppForm>;
};

export default MarketStateSnapshotCalendarDayTableRowPriceSubmissionAction;
